import React, {useEffect, useState} from 'react'

import type {Message} from './hooks/useChatMessageFormatting'

/** True once the live turn has anything besides an empty assistant shell. */
export function hasRealAssistantParts(
  msg: Pick<Message, 'content' | 'liveLeadMarkdown' | 'liveWorkingQueue' | 'timeline'>,
): boolean {
  if (msg.liveLeadMarkdown?.trim()) return true
  if ((msg.liveWorkingQueue ?? []).length > 0) return true
  if ((msg.timeline?.entries ?? []).length > 0) return true
  if (msg.content?.trim()) return true
  return false
}

export function useElapsedLabel(active: boolean): string | undefined {
  const [label, setLabel] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!active) {
      setLabel(undefined)
      return undefined
    }
    const start = Date.now()
    const id = window.setInterval(() => {
      setLabel(`${Math.round((Date.now() - start) / 1000)}s`)
    }, 1000)
    return () => window.clearInterval(id)
  }, [active])
  return label
}

export interface OmicsThinkingIndicatorProps {
  label: string
  elapsed?: string
}

/** assistant-ui thinking-indicator element: pulsing dot, label, optional elapsed badge. */
export const OmicsThinkingIndicator: React.FC<OmicsThinkingIndicatorProps> = ({label, elapsed}) => (
  <div
    className='omics-thinking-indicator'
    data-slot='thinking-indicator'
    data-testid='omics-ai-chat-thinking-indicator'
    role='status'
  >
    <span className='omics-thinking-indicator__dot' aria-hidden />
    <span key={label} className='omics-thinking-indicator__label'>
      {label}
    </span>
    {elapsed !== undefined ? (
      <span className='omics-thinking-indicator__elapsed'>{elapsed}</span>
    ) : null}
  </div>
)

export const OmicsAssistantThinking: React.FC = () => {
  const elapsed = useElapsedLabel(true)
  return <OmicsThinkingIndicator label='Thinking' elapsed={elapsed} />
}
